import { Component, EventEmitter, OnInit, Output, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FOR_ENV_OPTIONS, envIncludesProduction, forEnvBadgeClass, forEnvLabel } from '../../for-env.util';
import { LiveCallModelsService } from './live-call-models.service';
import { LiveCallModel } from './live-call-models.models';
import {
  ModelFormPanelComponent,
  ModelPanelMode,
} from './components/model-form-panel/model-form-panel.component';

/**
 * Models sub-section of the Agent tab: the catalog of realtime voice models a
 * prompt can target, with full management — add/edit/delete a model, toggle
 * its availability, and transfer the "in use" default (the model the live-call
 * agent runs today, seeded from the Python service's LIVE_MODEL).
 *
 * The backend protects the default row: it cannot be deleted or deactivated
 * until another model is made the default, so the agent is never left pointing
 * nowhere.
 */
@Component({
  selector: 'app-live-call-models',
  standalone: true,
  imports: [CommonModule, FormsModule, ModelFormPanelComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './live-call-models.component.html',
})
export class LiveCallModelsComponent implements OnInit {
  private modelsService = inject(LiveCallModelsService);

  /** Emits the model count after each load so the sub-tab shell can badge it. */
  @Output() countChange = new EventEmitter<number>();

  models = signal<LiveCallModel[]>([]);
  loading = signal(false);
  errorMsg = signal('');
  includeInactive = signal(true);

  // Panel
  panelOpen = signal(false);
  panelMode = signal<ModelPanelMode>('create');
  selectedModel = signal<LiveCallModel | null>(null);

  // Delete
  deleteTarget = signal<LiveCallModel | null>(null);
  deleting = signal(false);

  // Environment move (Test / Production / Both), pending the confirm dialog.
  envChangeTarget = signal<{ model: LiveCallModel; to: number } | null>(null);
  changingEnv = signal(false);

  // Env selector helpers for the template.
  readonly envOptions = FOR_ENV_OPTIONS;
  readonly envLabel = forEnvLabel;
  readonly envBadgeClass = forEnvBadgeClass;
  readonly envIncludesProduction = envIncludesProduction;

  ngOnInit(): void {
    this.loadModels();
  }

  loadModels(): void {
    this.loading.set(true);
    this.errorMsg.set('');
    this.modelsService.listModels(this.includeInactive()).subscribe({
      next: (res) => {
        this.models.set(res.models ?? []);
        this.countChange.emit(res.count ?? res.models?.length ?? 0);
        this.loading.set(false);
      },
      error: (err) => {
        this.models.set([]);
        this.countChange.emit(0);
        this.errorMsg.set(err?.message ?? 'Failed to load models.');
        this.loading.set(false);
      },
    });
  }

  toggleIncludeInactive(): void {
    this.includeInactive.set(!this.includeInactive());
    this.loadModels();
  }

  /** Provider badge label: "google" reads better as "Gemini" in this catalog. */
  providerLabel(model: LiveCallModel): string {
    if (model.provider === 'google') return 'Gemini';
    if (model.provider === 'openai') return 'OpenAI';
    return model.provider;
  }

  // ── panel ──────────────────────────────────────────────────────────────────

  openPanel(mode: ModelPanelMode, model: LiveCallModel | null): void {
    this.panelMode.set(mode);
    this.selectedModel.set(model);
    this.panelOpen.set(true);
  }

  closePanel(): void {
    this.panelOpen.set(false);
    this.selectedModel.set(null);
  }

  onSaved(): void {
    this.closePanel();
    this.loadModels();
  }

  // ── writes ─────────────────────────────────────────────────────────────────

  toggleActive(model: LiveCallModel): void {
    this.errorMsg.set('');
    this.modelsService.patchModel(model.id, { is_active: !model.isActive }).subscribe({
      next: () => this.loadModels(),
      error: (err) => this.errorMsg.set(err?.message ?? 'Could not update active status.'),
    });
  }

  /** Transfer the catalog default ("in use") to this model. */
  makeDefault(model: LiveCallModel): void {
    if (model.isDefault) return;
    this.errorMsg.set('');
    this.modelsService.patchModel(model.id, { is_default: true }).subscribe({
      next: () => this.loadModels(),
      error: (err) => this.errorMsg.set(err?.message ?? 'Could not set the default model.'),
    });
  }

  /** Row env selector changed — hold the move until the user confirms it. */
  requestEnvChange(model: LiveCallModel, to: number | string): void {
    const target = Number(to);
    if (!target || target === model.forEnv) return;
    this.envChangeTarget.set({ model, to: target });
  }

  /** Reload so the row's selector snaps back to the unchanged value. */
  cancelEnvChange(): void {
    this.envChangeTarget.set(null);
    this.loadModels();
  }

  executeEnvChange(): void {
    const change = this.envChangeTarget();
    if (!change) return;
    this.changingEnv.set(true);
    this.modelsService.patchModel(change.model.id, { for_env: change.to }).subscribe({
      next: () => {
        this.changingEnv.set(false);
        this.envChangeTarget.set(null);
        this.loadModels();
      },
      error: (err) => {
        this.changingEnv.set(false);
        this.envChangeTarget.set(null);
        this.errorMsg.set(err?.message ?? 'Environment change failed.');
        this.loadModels();
      },
    });
  }

  confirmDelete(model: LiveCallModel): void {
    this.deleteTarget.set(model);
  }

  cancelDelete(): void {
    this.deleteTarget.set(null);
  }

  executeDelete(): void {
    const target = this.deleteTarget();
    if (!target) return;
    this.deleting.set(true);
    this.modelsService.deleteModel(target.id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.cancelDelete();
        this.loadModels();
      },
      error: (err) => {
        this.deleting.set(false);
        this.cancelDelete();
        this.errorMsg.set(err?.message ?? 'Delete failed.');
      },
    });
  }

  trackByModel(_: number, model: LiveCallModel): number {
    return model.id;
  }
}
